import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { apiClient, cn, randomUUID } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen, TopBar } from '../components/mobile-screen'

const schema = z.object({
  subject: z.string().min(2).max(100),
  body: z.string().min(5).max(500),
  category: z.enum(['file', 'account', 'billing', 'other']),
})

type Category = z.infer<typeof schema>['category']

const CATEGORIES: Array<{ id: Category; label: string }> = [
  { id: 'file', label: '文件问题' },
  { id: 'account', label: '账户问题' },
  { id: 'billing', label: 'VIP相关' },
  { id: 'other', label: '其他' },
]

type CreateResp = { ticket_id: string }

export function TicketNewPage() {
  const navigate = useNavigate()
  const [category, setCategory] = useState<Category>('file')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      schema.parse({ category, subject, body })
      // gapi-restful-regroup:POST /gapi/user/tickets;body 字段映射
      // frontend(subject/body/category) → server(title/description/type) + idempotency_key
      const res = await apiClient.post<CreateResp>('/gapi/user/tickets', {
        title: subject,
        type: category,
        description: body,
        idempotency_key: randomUUID(),
      })
      navigate(`/tickets/${res.ticket_id}`, { replace: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : '提交失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppLayout>
      <MobileScreen>
        <TopBar title="新建工单" />

        <form data-testid="ZH7Zb" onSubmit={onSubmit} className="flex flex-1 flex-col gap-5 px-6 pt-4 pb-32">
          <section>
            <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">问题类型</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id)}
                  className={cn(
                    'rounded-full px-4 py-1.5 text-[12px] font-medium transition-colors',
                    category === c.id ? 'bg-[#19caad] text-white' : 'bg-[#F6F7F8] text-[#9CA3AF]',
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </section>

          <section>
            <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">标题</p>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="请输入问题标题"
              maxLength={100}
              className="h-12 w-full rounded-lg bg-[#F6F7F8] px-4 text-[14px] outline-none placeholder:text-[#B4B7BC]"
            />
          </section>

          <section>
            <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">问题描述</p>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="请详细描述您遇到的问题..."
              rows={6}
              maxLength={500}
              className="w-full resize-none rounded-lg bg-[#F6F7F8] p-4 text-[14px] outline-none placeholder:text-[#B4B7BC]"
            />
            <p className="mt-1 text-right text-[11px] text-[#9CA3AF]">{body.length} / 500</p>
          </section>

          <section>
            <p className="mb-2 text-[14px] font-semibold text-[#1A1A1A]">附件 (可选)</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="flex h-16 w-16 flex-col items-center justify-center rounded-lg border border-dashed border-[#D1D5DB] text-[#9CA3AF]"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
                <span className="text-[10px]">添加</span>
              </button>
            </div>
            <p className="mt-1 text-[11px] text-[#9CA3AF]">支持 jpg、png、pdf，最多 3 个附件，单个不超过 10MB</p>
          </section>

          {error && <p className="text-[13px] text-[#FF6B6B]">{error}</p>}
        </form>

        <div
          className="fixed inset-x-0 bottom-0 z-20 mx-auto max-w-[402px] bg-white px-6 pt-3"
          style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={onSubmit}
            disabled={busy || subject.length < 2 || body.length < 5}
            className="h-12 w-full rounded-lg bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
          >
            {busy ? '提交中…' : '提交工单'}
          </button>
        </div>
      </MobileScreen>
    </AppLayout>
  )
}
